// The Text changes page: the script tag of the user, and the submissions from the embed script.
import "./analytics";
import type { EditKey, ReceivedSubmission } from "@feedmark/shared/api";
import { showAccount } from "./account";
import { client, succeeded } from "./api";
import { $, h } from "./dom";
import "./own-embed";

function fail(message: string) {
  $("alert").textContent = message;
  $("alert").hidden = false;
}

// Changes the label of a button for a short time after a copy.
async function copy(button: HTMLButtonElement, text: string) {
  const label = button.textContent;
  await navigator.clipboard.writeText(text).then(
    () => {
      button.textContent = "Copied";
    },
    () => fail("The browser did not permit the copy."),
  );
  setTimeout(() => {
    button.textContent = label;
  }, 1500);
}

function plural(count: number, noun: string): string {
  return count === 1 ? `1 ${noun}` : `${count} ${noun}s`;
}

// A submission is text from a host page: it is untrusted. All of it goes in with `textContent`.
// `removed` runs after the user deleted the submission.
function card({ id, receivedAt, submission }: ReceivedSubmission, removed: () => void): HTMLElement {
  // A submission from before the page comments has no `comments`.
  const comments = submission.comments ?? [];
  const counts = [plural(submission.changes.length, "change"), plural(comments.length, "comment")];
  const copyJson = h("button", { className: "small", textContent: "Copy the JSON" });
  copyJson.addEventListener("click", () => copy(copyJson, JSON.stringify(submission, null, 2)));
  const remove = h("button", { className: "ghost small", textContent: "Delete" });
  const element = h(
    "article",
    { className: "submission" },
    h("h3", {}, submission.pageTitle || submission.pageUrl),
    // The app Worker accepts only http and https URLs.
    h("a", { href: submission.pageUrl, target: "_blank", rel: "noopener noreferrer" }, submission.pageUrl),
    h(
      "p",
      { className: "facts" },
      `${submission.reviewer} · ${new Date(receivedAt).toLocaleString()} · ${counts.join(" · ")}`,
    ),
    h(
      "ul",
      { className: "changes" },
      ...submission.changes.map((change) =>
        h(
          "li",
          {},
          h("code", { className: "muted" }, change.selector),
          h("p", {}, h("del", {}, change.oldText)),
          h("p", {}, h("ins", {}, change.newText || "(no text)")),
        ),
      ),
      ...comments.map((comment) =>
        h(
          "li",
          {},
          h("code", { className: "muted" }, comment.element?.selector ?? "Comment about the page"),
          comment.element?.text ? h("p", { className: "muted" }, comment.element.text) : null,
          h("p", { className: "comment" }, comment.body),
        ),
      ),
    ),
    h("div", { className: "row" }, copyJson, remove),
  );
  remove.addEventListener("click", async () => {
    remove.disabled = true;
    const request = client.api.submissions[":id"].$delete({ param: { id } });
    if (await succeeded(request, fail, "The submission was not deleted.")) {
      element.remove();
      removed();
    }
    remove.disabled = false;
  });
  return element;
}

// The domain of the host page. The app Worker accepts only URLs that are valid.
function domainOf({ submission }: ReceivedSubmission): string {
  try {
    return new URL(submission.pageUrl).host;
  } catch {
    return "Unknown website";
  }
}

// One group for each domain: a user can have the same script tag on many websites.
// The submissions are newest first, thus the first group has the newest submission.
function groups(submissions: ReceivedSubmission[]): HTMLElement[] {
  const byDomain = new Map<string, ReceivedSubmission[]>();
  for (const item of submissions) {
    const domain = domainOf(item);
    byDomain.set(domain, [...(byDomain.get(domain) ?? []), item]);
  }
  return [...byDomain].map(([domain, items]) => {
    const count = h("p", { className: "facts" });
    const group = h("section", { className: "site" }, h("h2", {}, domain), count);
    const update = () => {
      const cards = group.querySelectorAll(".submission").length;
      count.textContent = cards === 1 ? "1 submission" : `${cards} submissions`;
      if (cards === 0) group.remove();
    };
    group.append(...items.map((item) => card(item, update)));
    update();
    return group;
  });
}

// The edit link of the home page of the domain. The editor can then go to the other host pages.
function editLink(domain: string, editKey: string): string {
  return `https://${domain}/?feedmark=${editKey}`;
}

// The edit key of one domain, as an edit link that the user copies and sends to the editor.
function editKeyRow({ domain, editKey }: EditKey): HTMLElement {
  const link = h("code", {}, editLink(domain, editKey));
  const copyLink = h("button", { className: "small", textContent: "Copy the edit link" });
  copyLink.addEventListener("click", () => copy(copyLink, link.textContent ?? ""));
  const renew = h("button", { className: "ghost small", textContent: "Make a new key" });
  renew.title = "The old edit link stops. Send the new one to the editor.";
  const remove = h("button", { className: "ghost small", textContent: "Remove" });
  const row = h(
    "li",
    { className: "edit-key" },
    h("strong", {}, domain),
    link,
    h("div", { className: "row" }, copyLink, renew, remove),
  );
  renew.addEventListener("click", async () => {
    renew.disabled = true;
    const request = client.api["edit-keys"][":domain"].$put({ param: { domain } });
    const response = await succeeded(request, fail, "The new edit key was not made.");
    if (response) link.textContent = editLink(domain, (await response.json()).editKey);
    renew.disabled = false;
  });
  remove.addEventListener("click", async () => {
    remove.disabled = true;
    const request = client.api["edit-keys"][":domain"].$delete({ param: { domain } });
    if (await succeeded(request, fail, "The domain was not removed.")) row.remove();
    remove.disabled = false;
  });
  return row;
}

function showEditKeys(editKeys: EditKey[]) {
  const list = $("edit-keys");
  list.replaceChildren(...editKeys.map(editKeyRow));
  const form = $<HTMLFormElement>("add-domain");
  const input = $<HTMLInputElement>("domain");
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const request = client.api["edit-keys"].$post({ json: { domain: input.value } });
    const response = await succeeded(request, fail, "The domain was not added.");
    if (!response) return;
    const added = await response.json();
    // A domain that has a key keeps it: the list shows it one time.
    for (const row of list.querySelectorAll("strong"))
      if (row.textContent === added.domain) row.parentElement?.remove();
    list.append(editKeyRow(added));
    input.value = "";
    $("alert").hidden = true;
  });
}

async function load() {
  const user = await showAccount();
  $("loading").hidden = true;
  if (!user) {
    $("logged-out").hidden = false;
    return;
  }
  const response = await succeeded(client.api.submissions.$get(), fail, "The submissions did not load.");
  if (!response) return;
  const { scriptTag, editKeys, submissions } = await response.json();
  $("script-tag").textContent = scriptTag;
  const copyTag = $<HTMLButtonElement>("copy-tag");
  copyTag.addEventListener("click", () => copy(copyTag, scriptTag));
  showEditKeys(editKeys);
  const list = $("submissions");
  list.append(...groups(submissions));
  // A group goes away when the user deletes its last submission.
  const showEmpty = () => {
    $("no-submissions").hidden = list.childElementCount > 0;
  };
  new MutationObserver(showEmpty).observe(list, { childList: true });
  showEmpty();
  $("board").hidden = false;
}

load();
